import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Box, BoxSection, Typography } from "@aviary";
import { CatalogBrands } from "@shared/components/CatalogSearch/BrandsBox/CatalogBrands";
import { Header } from "@shared/components/Header";
import { NavigationButtons } from "@shared/components/NavigationButtons";
import { useBrandsQuery } from "@shared/data/queries";
import type { Variables as BrandsVariablesType } from "@shared/data/queries/BrandsQuery/index";
import { useBreakpoints } from "@shared/hooks";
import { l } from "@shared/locales/i18n";

import type { Brand } from "../CatalogSearch";
import { SkeletonColumnList } from "../SkeletonColumnList";

import { BrandsList } from "./BrandsList";
import { SkeletonTopBrands } from "./CatalogBrands/SkeletonTopBrands";

import * as styles from "./BrandsBox.styles";

const NUMBER_OF_BRANDS_TO_SHOW = 24;
const NUMBER_OF_BRANDS_TO_SHOW_MOBILE = 12;

interface Props {
  onBrandClick: (brand: Brand) => void;
  activeBrands: Brand[];
  columnCount?: number;
}

const BrandsBox = ({ onBrandClick, activeBrands, columnCount }: Props) => {
  const { phoneLarge } = useBreakpoints();
  const { t } = useTranslation();
  const [afterCursorStack, setAfterCursorStack] = useState<string[]>([]);
  const numberOfBrandsToShow = phoneLarge.lessThan
    ? NUMBER_OF_BRANDS_TO_SHOW_MOBILE
    : NUMBER_OF_BRANDS_TO_SHOW;

  const getTopBrandsQueryVariables = (): BrandsVariablesType => ({
    first: 8,
    filters: { list: "TOP_BRANDS", active: true },
  });

  const getBrandsQueryVariables = (): BrandsVariablesType => {
    const afterCursorStackLength = afterCursorStack.length;
    const targetAfterCursor =
      afterCursorStackLength && afterCursorStack[afterCursorStackLength - 1];

    return {
      filters: {
        active: true,
      },
      first: numberOfBrandsToShow,
      ...(targetAfterCursor && { after: targetAfterCursor }),
    };
  };

  const isInitialPage = afterCursorStack.length === 0;

  const { loading, data } = useBrandsQuery({
    variables: isInitialPage ? getTopBrandsQueryVariables() : getBrandsQueryVariables(),
    fetchPolicy: "no-cache",
  });

  const brandsData = data?.viewer?.brands?.nodes;
  const pageInfo = data?.viewer?.brands?.pageInfo;

  const getContent = () => {
    if (isInitialPage) {
      return loading ? (
        <SkeletonTopBrands data-testid="skeleton-top-brands" />
      ) : (
        <CatalogBrands
          brands={brandsData}
          onBrandClick={onBrandClick}
          activeBrands={activeBrands}
        />
      );
    }

    return loading ? (
      <SkeletonColumnList numberOfColumns={columnCount} data-testid="skeleton-column-list" />
    ) : (
      <BrandsList columnCount={columnCount} brands={brandsData} onBrandClick={onBrandClick} />
    );
  };

  const handleOnBack = () =>
    setAfterCursorStack(afterCursorStack.slice(0, afterCursorStack.length - 1));

  const handleOnNext = () =>
    setAfterCursorStack([...afterCursorStack, isInitialPage ? undefined : pageInfo.endCursor]);

  const renderTitle = () => (
    <Typography type="h5" css={styles.title}>
      {isInitialPage ? t(l.sharedCatalog.Popular) : t(l.sharedCatalog.All)}
      <span css={styles.titleHighlighted}>{t(l.sharedCatalog.brands)}</span>
    </Typography>
  );

  const isNextDisabled = () => {
    if (loading) {
      return true;
    } else if (isInitialPage) {
      return !brandsData;
    }

    return !pageInfo?.hasNextPage;
  };

  return (
    <Box css={styles.brandsList}>
      <BoxSection css={styles.boxSection}>
        <Header css={styles.header}>
          {renderTitle()}
          <NavigationButtons
            backDisabled={isInitialPage || loading}
            nextDisabled={isNextDisabled()}
            onBack={handleOnBack}
            onNext={handleOnNext}
          />
        </Header>
        {getContent()}
      </BoxSection>
    </Box>
  );
};

export { BrandsBox };
